import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Container, Row, Col } from "reactstrap";
import { useHistory, useParams } from "react-router-dom";
import moment from "moment";
import toast from "react-hot-toast";

import "./Details.scss";

import { PageTitles } from "constants";
import { APP_ROUTES } from "helpers/routeHelpers";
import { getClickAnalyticsDetailsByDate } from "../../../api/api.service";
import { HeadingWithBackArrow } from "components/HeadingWithBackArrow";
import { LoaderFallbackUI } from "components/LoaderFallbackUI";
import { convertDateTime, dateTimeFormat } from "utils/dateFormatUtils";

import facebookIcon from "assets/images/app/common/facebook.svg";
import instagramIcon from "assets/images/app/common/instagram.svg";
import iosAppleIcon from "assets/images/app/common/ios-apple.svg";
import playStoreIcon from "assets/images/app/common/play-store.svg";
import webIcon from "assets/images/app/common/web.svg";
import websitesIcon from "assets/images/app/common/websites.png";
import { COUNTRIES } from "constants";

const Details = () => {
  const history = useHistory();
  const { date } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!moment(date, dateTimeFormat.apiDateFormat, true).isValid()) {
      toast.error("Invalid date format. Please select a valid date.");
      history.push(APP_ROUTES.CLICK_ANALYTICS);
      return;
    }
    fetchDetailsData();
  }, [date]);

  const fetchDetailsData = async () => {
    setLoading(true);
    try {
      const payload = { Date: date };
      const response = await getClickAnalyticsDetailsByDate(payload);

      if (response) {
        setData(response);
      } else {
        setData({
          day: date,
          totalClicks: 0,
          platforms: [],
          countryCodes: [],
          referrers: [],
        });
      }
    } catch (error) {
      console.error("Error fetching details:", error);
      setData({
        day: date,
        totalClicks: 0,
        platforms: [],
        countryCodes: [],
        referrers: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const backButtonHandler = () => {
    history.push(APP_ROUTES.CLICK_ANALYTICS);
  };

  if (loading) {
    return <LoaderFallbackUI />;
  }

  if (!data) {
    return null;
  }

  const displayData = {
    date: convertDateTime({
      date: data.day,
      customFormat: dateTimeFormat.americanFormat,
    }),
    totalClicks: data.totalClicks || 0,
    devices: [
      { name: "Android", clicks: 0, icon: playStoreIcon },
      { name: "IOS", clicks: 0, icon: iosAppleIcon },
      { name: "Web", clicks: 0, icon: webIcon },
    ],
    countries: [],
    sources: [],
  };

  data.platforms?.forEach((platform) => {
    const deviceIndex = displayData.devices.findIndex(
      (d) => d.name.toLowerCase() === platform.name.toLowerCase()
    );
    if (deviceIndex !== -1) {
      displayData.devices[deviceIndex].clicks = platform.count;
    }
  });

  data.countryCodes?.forEach((country) => {
    displayData.countries.push({
      code: country.name,
      name: COUNTRIES.find((c) => c.code === country.name)?.label || "-",
      clicks: country.count,
    });
  });

  data.referrers?.forEach((referrer) => {
    let icon = websitesIcon;
    const refName = referrer.name.toLowerCase();

    if (refName.includes("facebook")) {
      icon = facebookIcon;
    } else if (refName.includes("instagram")) {
      icon = instagramIcon;
    }

    displayData.sources.push({
      name: referrer.name,
      clicks: referrer.count,
      icon: icon,
    });
  });

  return (
    <>
      <div className="page-content">
        <Helmet>
          <title>{PageTitles.CLICK_ANALYTICS_DETAILS}</title>
        </Helmet>
        <Container fluid className="click-analytics-details">
          <HeadingWithBackArrow
            backArrowClickHandler={backButtonHandler}
            headingTitle={"Click Analytic Details"}
            showToggle={false}
          />

          <div className="total-clicks-section">
            <h2 className="total-clicks-title">
              Total Clicks | {displayData.totalClicks} Clicks
            </h2>
          </div>

          <div className="device-section mb-4">
            <div className="analytics-card">
              <h3 className="analytics-card-title">Click by device</h3>
              <Row>
                {displayData.devices.map((device, index) => (
                  <Col lg={4} md={4} sm={12} key={index}>
                    <div className="analytics-item">
                      <div className="analytics-item-icon">
                        <img src={device.icon} alt={device.name} />
                      </div>
                      <div className="analytics-item-content">
                        <span className="analytics-item-name">
                          {device.name}
                        </span>
                        <span className="analytics-item-value">
                          {device.clicks}{" "}
                          {device.clicks === 1 ? "Click" : "Clicks"}
                        </span>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </div>

          <Row>
            <Col lg={6} md={6} sm={12} className="mb-4">
              <div className="analytics-card">
                <h3 className="analytics-card-title">Countries</h3>
                <div className="analytics-list">
                  {displayData.countries.length > 0 ? (
                    displayData.countries.map((country, index) => (
                      <div key={index} className="analytics-item">
                        <div className="analytics-item-flag">
                          <img
                            loading="lazy"
                            width="24"
                            srcSet={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png 2x`}
                            src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`}
                            alt={country.name}
                          />
                        </div>
                        <div className="analytics-item-content">
                          <span className="analytics-item-name">
                            <span className="analytics-item-name-code">
                              {country.code}
                            </span>
                            {country.name}
                          </span>
                          <span className="analytics-item-value">
                            {country.clicks}{" "}
                            {country.clicks === 1 ? "Click" : "Clicks"}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-data-text">No countries</div>
                  )}
                </div>
              </div>
            </Col>

            <Col lg={6} md={6} sm={12} className="mb-4">
              <div className="analytics-card">
                <h3 className="analytics-card-title">Source</h3>
                <div className="analytics-list">
                  {displayData.sources.length > 0 ? (
                    displayData.sources.map((source, index) => (
                      <div key={index} className="analytics-item">
                        <div className="analytics-item-icon">
                          <img src={source.icon} alt={source.name} />
                        </div>
                        <div className="analytics-item-content">
                          <span className="analytics-item-name">
                            {source.name}
                          </span>
                          <span className="analytics-item-value">
                            {source.clicks}{" "}
                            {source.clicks === 1 ? "Click" : "Clicks"}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-data-text">No sources</div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default Details;
